import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  ApiEcosystemModel,
  AuthData,
  defaultState,
  Notification,
  User,
  ContactUsData,
  LoginData,
  ExternalApiDetails,
  Application,
  CombineApplicationsView,
  getAvailablePersonasFromApis,
} from './index';
import { Inject, Injectable } from '@angular/core';
import { DataService } from '../services/data.service';
import { BannerService } from '../services/banner.service';
import {
  GetApiDetail,
  RemoveNotification,
  RemoveNotifications,
  SetNotification,
  SetSelectedRoute,
  SetUser,
  SetAuthData,
  ShowNavbarText,
  SetSignupData,
  GetExternalApiData,
  GetExternalApplicationsData,
  Login,
  CreateUser,
  SetSelectedApi,
  SetResetPasswordEmail,
  SendResetPasswordEmail,
  CreateApplication,
  AzureLogin,
  AzureLogout,
  SubscribeToOneApiForManyApplications,
  SetSelectedApplication,
  GetUserFromSession,
  SendContactUsEmail,
  DownloadDocument,
  UpdateUser,
  GetCachedUser,
  AddUri,
  UnSubscribeAPI,
  GetApplicationByID,
  DeleteApplication,
  GenerateClientId,
  RegenerateClientId,
  ResetSelectedApplication,
  SetClientSecret,
  ResetClientSecret,
  InitSso,
  SubscribeToManyApisForOneApplication,
  UpdateAppWebsite,
  UpdateAppOwner,
  UpdateDelegateIds,
  GetContactUsCategories,
  SetTeamGroup,
  AddUserToTeamGroup,
  RemoveUserFromTeamGroup,
  PromoteUserToOwner,
  GeneratePayerClientId,
  GetBannerData,
  SetBannerData,
} from './api-eco.actions';
import { tap, map, catchError, finalize, shareReplay, switchMap } from 'rxjs/operators';
import { patch } from '@ngxs/store/operators';
import { SsoService } from '../services/sso.service';
import { LoginService } from '../services/login.service';
import { Observable, of } from 'rxjs';
import { MSAL_GUARD_CONFIG, MsalGuardConfiguration, MsalService } from '@azure/msal-angular';
import { RedirectRequest } from '@azure/msal-browser';
import { Location } from '@angular/common';
import { environment } from 'src/environments/environment';
import { ProgressSpinnerDialogService } from '../services/progress-spinner-dialog.service';
import { ContextUpdateType, UserContextService } from '../services/user-context.service';
import { APIELogService } from '../services/apie-log.service';
import { BrowserStorageType } from '../services/browser-storage.service';
import { AlertMessages } from 'src/assets/data/alert-messages';
import { Router } from '@angular/router';
import { ResetUserState, SetUserFromUserState } from './user-store/user.actions';
import { ResetRouteState } from './route-store/route.actions';

@State<ApiEcosystemModel>({
  name: 'apiEcosystem',
  defaults: defaultState,
})
@Injectable()
export class ApiEcoState {
  b2cPolicies: any;
  clientId: string;
  private externalApiDataRequest$?: Observable<any[]>;
  private externalApplicationsRequest$?: Observable<CombineApplicationsView>;
  private bannerDataRequest$?: Observable<any>;
  private applicationByIdRequests = new Map<string, Observable<Application>>();

  constructor(
    private service: DataService,
    private ssoService: SsoService,
    private loginService: LoginService,
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private location: Location,
    private authService: MsalService,
    private progressDialogService: ProgressSpinnerDialogService,
    private userContextService: UserContextService,
    private logService: APIELogService,
    private router: Router,
    private bannerService: BannerService
  ) {
    this.b2cPolicies = environment.b2cPolicies;
    this.clientId = environment.clientId;
  }

  @Selector()
  static getShowNavbarText(state: ApiEcosystemModel): boolean {
    return state.showNavbarText;
  }

  // Seems it is not used anywhere but used Contact Us Interface
  @Selector()
  static getSignupData(state: ApiEcosystemModel): any {
    return state.signupData;
  }

  @Selector()
  static getLoginData(state: ApiEcosystemModel): LoginData {
    return state.loginData;
  }

  @Selector()
  static getResetPasswordEmail(state: ApiEcosystemModel): string {
    return state.resetPasswordEmail;
  }

  @Selector()
  static getSelectedRoute(state: ApiEcosystemModel): string {
    return state.selectedRoute;
  }

  @Selector()
  static getNotifications(state: ApiEcosystemModel): Notification[] {
    return state.notifications;
  }

  @Selector()
  static getAuthData(state: ApiEcosystemModel): AuthData {
    return state.authData;
  }

  @Selector()
  static getUser(state: ApiEcosystemModel): User {
    return state.user;
  }

  @Selector()
  static getApis(state: ApiEcosystemModel): ExternalApiDetails[] {
    return state.apis;
  }

  @Selector()
  static getAvailablePersonas(state: ApiEcosystemModel): string[] {
    return getAvailablePersonasFromApis(state.apis);
  }

  @Selector()
  static getSelectedApiDetail(state: ApiEcosystemModel): ExternalApiDetails {
    return state.selectedApiDetail;
  }

  @Selector()
  static getExternalApplications(state: ApiEcosystemModel): Application[] | CombineApplicationsView {
    return state.applications;
  }

  @Selector()
  static getSelectedApplicationDetail(state: ApiEcosystemModel): any {
    return state.selectedApplicationDetail;
  }

  @Selector()
  static getDownloadDocumentURI(state: ApiEcosystemModel): any {
    return state.downloadDocumentURI;
  }

  @Selector()
  static getDownloadDocument(state: ApiEcosystemModel): { uri: string; attachmentId: string } {
    return { uri: state.downloadDocumentURI, attachmentId: state.downloadDocumentAttachmentId };
  }

  @Selector()
  static getClientSecret(state: ApiEcosystemModel): any {
    return state.clientSecret;
  }

  @Selector()
  static getContactUsCategories(state: ApiEcosystemModel): any {
    return state.contactUsCategories;
  }

  @Selector()
  static getBanner(state: ApiEcosystemModel): any {
    return state.bannerData;
  }

  @Action(SetSelectedApplication)
  setSelectedApplication({ patchState }: StateContext<ApiEcosystemModel>, action: SetSelectedApplication) {
    patchState({ selectedApplicationDetail: action.app });
  }

  @Action(SetClientSecret)
  setClientSecret({ patchState }: StateContext<ApiEcosystemModel>, action: SetClientSecret) {
    patchState({ clientSecret: action.secret });
  }

  @Action(ResetClientSecret)
  resetClientSecret({ patchState }: StateContext<ApiEcosystemModel>) {
    patchState({
      clientSecret: undefined,
    });
  }

  @Action(ResetSelectedApplication)
  resetSelectedApplication({ patchState }: StateContext<ApiEcosystemModel>) {
    patchState({ selectedApplicationDetail: undefined });
  }

  @Action(GetExternalApiData)
  getExternalApiData({ setState, dispatch }: StateContext<ApiEcosystemModel>) {
    if (this.externalApiDataRequest$) {
      return this.externalApiDataRequest$;
    }

    const currentApiId = this.router?.url?.includes('/apis/')
      ? this.router.url.split('?')[0]?.split('/')[2]
      : undefined;

    this.externalApiDataRequest$ = this.service.getExternalApiData().pipe(
      tap((result: any[]) => {
        if (result) {
          try {
            let selectedApi: ExternalApiDetails | undefined;

            const publicAPI: ExternalApiDetails = result.find(
              (api: ExternalApiDetails) => api.visibility.toLocaleLowerCase() === 'public'
            );
            if (publicAPI) {
              selectedApi = publicAPI;
            }
            const routeApiId = currentApiId || this.router?.url?.split('/')[2];
            const routeSelectedApi = result.find(api => api?.id === routeApiId);
            if (routeSelectedApi) {
              selectedApi = routeSelectedApi;
            }
            if (selectedApi) {
              dispatch(new GetApiDetail(selectedApi.id));
            }
            setState(patch({ apis: result }));
          } catch (error) {
            this.logService.log(
              `Error retreiving API Data. Error Detail: ${error}\nPayload:\n${JSON.stringify(result, null, '\t')}... `,
              'error',
              `HTTP -> API DATA 🚨 🚨 🚨`
            );
          }
        }
      }),
      finalize(() => {
        this.externalApiDataRequest$ = undefined;
      }),
      shareReplay(1)
    );

    return this.externalApiDataRequest$;
  }

  @Action(GetExternalApplicationsData)
  getExternalApplicationsData({ patchState, getState }: StateContext<ApiEcosystemModel>) {
    if (this.externalApplicationsRequest$) {
      return this.externalApplicationsRequest$;
    }

    this.externalApplicationsRequest$ = this.service.getExternalApplicationsData().pipe(
      tap((result: CombineApplicationsView) => {
        patchState({
          ...getState(),
          applications: result,
        });
      }),
      finalize(() => {
        this.externalApplicationsRequest$ = undefined;
      }),
      shareReplay(1)
    );

    return this.externalApplicationsRequest$;
  }

  @Action(SetSelectedRoute)
  setSelectedRoute({ patchState }: StateContext<ApiEcosystemModel>, action: SetSelectedRoute) {
    patchState({ selectedRoute: action.route });
  }

  @Action(ShowNavbarText)
  toggleNavbarText({ getState, patchState }: StateContext<ApiEcosystemModel>) {
    patchState({
      showNavbarText: !getState().showNavbarText,
    });
  }

  @Action(SetSignupData)
  setSignupData({ getState, patchState }: StateContext<ApiEcosystemModel>, action: SetSignupData) {
    patchState({
      signupData: action.signupData,
    });
  }

  @Action(SetResetPasswordEmail)
  setResetPasswordEmail({ getState, patchState }: StateContext<ApiEcosystemModel>, action: SetResetPasswordEmail) {
    patchState({
      resetPasswordEmail: action.email,
    });
  }

  @Action(SendResetPasswordEmail)
  sendResetPasswordEmail(
    { getState, patchState, dispatch }: StateContext<ApiEcosystemModel>,
    action: SendResetPasswordEmail
  ) {
    return this.service.sendResetPasswordEmal(action.email).pipe(
      map(result => {
        patchState({
          resetPasswordEmail: action.email,
        });
        dispatch(
          new SetNotification({
            error: false,
            warning: false,
            message: AlertMessages.SuccessResetPassword,
            title: 'Success',
          })
        );

        dispatch(new SetSelectedRoute('home'));
        return result;
      }),
      catchError(() => {
        dispatch(
          new SetNotification({
            error: true,
            warning: false,
            message: AlertMessages.ErrorResetPassword,
            title: 'Error',
          })
        );
        return of(undefined);
      })
    );
  }

  @Action(SetNotification)
  setNotification({ getState, patchState }: StateContext<ApiEcosystemModel>, action: SetNotification) {
    const notifications = [...getState().notifications];
    notifications.push(action.notification);
    patchState({
      notifications,
    });
  }

  @Action(RemoveNotification)
  removeNotification({ getState, patchState }: StateContext<ApiEcosystemModel>, action: RemoveNotification) {
    const notifications = [...getState().notifications];
    notifications.splice(
      notifications.findIndex(
        (item: Notification) =>
          item.duration === action.notification.duration &&
          item.error === action.notification.error &&
          item.title === action.notification.title &&
          item.message === action.notification.message,
        1
      )
    );
    patchState({
      notifications,
    });
  }

  @Action(RemoveNotifications)
  removeNotifications({ getState, patchState }: StateContext<ApiEcosystemModel>) {
    const notifications = [...getState().notifications];
    notifications.length = 0;
    patchState({
      notifications,
    });
  }

  @Action(SetAuthData)
  setAuthData({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: SetAuthData) {
    this.logService.log(`Storing authorization data for future retrieval... `, 'info', `USER_CONTEXT -> SSO AUTH`);
    this.userContextService.updateContext(ContextUpdateType.AUTH_DATA, action.authData, BrowserStorageType.LOCAL);

    patchState({
      authData: { ...getState().authData, ...action.authData },
    });

    // Persist auth data to UserState (localStorage via storage plugin)
    dispatch(new SetUserFromUserState({ ...getState().user }));
  }

  @Action(CreateUser)
  createUser({ getState, dispatch }: StateContext<ApiEcosystemModel>, action: CreateUser) {
    const { homeAccountId, idToken, ...newSignupData } = action.signupData;
    return this.service.createUser(newSignupData).pipe(
      map((result: any) => {
        dispatch(
          new SetNotification({
            error: false,
            warning: false,
            message: AlertMessages.SuccessUserRegistration,
            title: 'Success',
          })
        );
        dispatch(
          new Login({
            email: action.signupData.email,
            idToken,
            homeAccountId: '',
            updateUserData: undefined,
          })
        );
        dispatch(new SetUser(result.user));
      }),
      catchError(() => {
        dispatch(
          new SetNotification({
            error: true,
            warning: false,
            message: AlertMessages.ErrorUserRegistration,
            title: 'Error',
          })
        );
        return of(undefined);
      })
    );
  }

  @Action(AzureLogout)
  azureLogout({ getState, dispatch }: StateContext<ApiEcosystemModel>, action: AzureLogout) {
    if (getState().user.isAuthenticated) {
      this.loginService.logout().subscribe(result => {
        if (result) {
          dispatch(
            new SetUser({
              _id: 'aVal',
              email: '',
              firstName: '',
              lastName: '',
              displayName: '',
              isAuthenticated: false,
              userRole: undefined,
            })
          );
          dispatch(new ResetUserState());
          dispatch(new ResetRouteState());
          this.authService.logout();
        }
      });
    }
  }

  @Action(AzureLogin)
  azureLogin({ getState, patchState }: StateContext<ApiEcosystemModel>, action: AzureLogin) {
    this.logService.log('Logging In to Azure', 'info', 'USER CONTEXT -> APIE STATE | LOGIN IN PROGRESS');

    const userFlowRequest = {
      ...action.userFlowRequest,
      scopes: [this.clientId],
    };
    if (this.msalGuardConfig.authRequest) {
      this.authService.loginRedirect({ ...this.msalGuardConfig.authRequest, ...userFlowRequest } as RedirectRequest);
    } else {
      this.authService.loginRedirect(userFlowRequest);
    }
  }

  @Action(SetUser)
  setUser({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: SetUser) {
    patchState({
      user: { ...getState().user, ...action.user },
    });

    // Persist user to UserState (localStorage via storage plugin)
    dispatch(new SetUserFromUserState({ ...getState().user, ...action.user }));
  }

  @Action(GetUserFromSession)
  getUserFromSession({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: GetUserFromSession) {
    const tokenInfo = this.userContextService.getTokenInfo();
    // Fall back to the token persisted in NGXS UserState when global_context.token is missing
    // (e.g. after localStorage was cleared on token expiry but NGXS state was already rehydrated)
    const savedUserToken = tokenInfo.token || getState().user.userToken;

    if (savedUserToken) {
      // Re-sync the token to global_context if it was missing there
      if (!tokenInfo.token) {
        this.userContextService.updateContext(ContextUpdateType.TOKEN, savedUserToken, BrowserStorageType.LOCAL);
      }
      this.logService.log(
        'Token found from existing session...',
        'success',
        `USER CONTEXT -> APIE STATE > LOGGED IN 🍺   `
      );
      const userData = {
        _id: 'aVal',
        email: '',
        userToken: savedUserToken,
      };
      dispatch(new GetCachedUser(userData));
    } else {
      // Check if current account is active. This could occur right after login before token value is set in any context.
      const currentAcount = this.authService.instance.getActiveAccount();

      if (!currentAcount) {
        this.logService.log(
          'Get user from session: No TOKEN or active account Found',
          'error',
          `USER_CONTEXT -> APIE STATE > LOGIN FAILURE 😳 `
        );
      } else {
        this.logService.log(
          'Active account already set. Getting user from cache is not requred.',
          'info',
          `USER_CONTEXT -> LOGGED IN 🤜 `
        );
      }
    }
  }

  @Action(GetCachedUser)
  getCachedUser({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: GetCachedUser) {
    if (action.user.userToken) {
      this.loginService.getCachedUser(action.user.userToken).subscribe(cachedUser => {
        if (cachedUser) {
          patchState({
            user: {
              ...cachedUser,
              isAuthenticated: true,
            },
          });
          dispatch(new GetExternalApplicationsData());
        }
      });
    }
  }

  @Action(GeneratePayerClientId)
  generatePayerClientId(
    { getState, patchState, dispatch }: StateContext<ApiEcosystemModel>,
    action: GeneratePayerClientId
  ) {
    if (action.generatePayerClientIdData.appId) {
      const { isPrimary, appId } = action.generatePayerClientIdData;
      const payload = {
        environment: isPrimary ? 'primary' : 'sandbox',
        jwksUrl: action.generatePayerClientIdData.jwksUrl,
        clientFor: getState().selectedApplicationDetail?.applicationPersona,
      };
      if (isPrimary) {
        this.service.generatePayerClientId(appId, payload).subscribe(response => {
          if (response) {
            const selectedApp = {
              ...getState().selectedApplicationDetail,
              prodClientId: response.clientId,
              prodClientSecret: response.clientSecret,
            };
            dispatch(new SetSelectedApplication(selectedApp));
            dispatch(new GetExternalApplicationsData());
            patchState({ clientSecret: response.clientSecret });
          }
        });
      } else {
        this.service.generatePayerClientId(appId, payload).subscribe(response => {
          if (response) {
            const selectedApp = {
              ...getState().selectedApplicationDetail,
              sandboxClientId: response.clientId,
            };
            dispatch(new SetSelectedApplication(selectedApp));
            dispatch(new GetExternalApplicationsData());
            patchState({ clientSecret: response.clientSecret });
          }
        });
      }
    }
  }

  @Action(GenerateClientId)
  generateClientId({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: GenerateClientId) {
    if (action.generateClientIdData.appId) {
      const { isPrimary, appId } = action.generateClientIdData;
      const body = {
        environment: isPrimary ? 'primary' : 'sandbox',
      };
      if (isPrimary) {
        this.service.generateClientId(appId, body).subscribe(response => {
          if (response) {
            const selectedApp = {
              ...getState().selectedApplicationDetail,
              prodClientId: response.clientId,
              prodClientSecret: response.clientSecret,
            };
            dispatch(new SetSelectedApplication(selectedApp));
            dispatch(new GetExternalApplicationsData());
            patchState({ clientSecret: response.clientSecret });
          }
        });
      } else {
        this.service.generateClientId(appId, body).subscribe(response => {
          if (response) {
            const selectedApp = {
              ...getState().selectedApplicationDetail,
              sandboxClientId: response.clientId,
            };
            dispatch(new SetSelectedApplication(selectedApp));
            dispatch(new GetExternalApplicationsData());
            patchState({ clientSecret: response.clientSecret });
          }
        });
      }
    }
  }

  @Action(RegenerateClientId)
  regenerateClientId({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: RegenerateClientId) {
    if (action.regenerateClientIdData.appId) {
      const { isPrimary, appId } = action.regenerateClientIdData;
      const body = {
        environment: isPrimary ? 'primary' : 'sandbox',
      };
      this.service.regenerateClientId(appId, body).subscribe(response => {
        if (response) {
          patchState({ clientSecret: response.clientSecret });
        }
      });
    }
  }

  @Action(UpdateUser)
  updateUser({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: UpdateUser) {
    const tokenInfo = this.userContextService.getTokenInfo();
    const { homeAccountId, idToken, ...newUpdateData } = action.updateUserData;
    return this.loginService.updateUser(newUpdateData, tokenInfo.token).pipe(
      map((result: any) => {
        dispatch(
          new SetNotification({
            error: false,
            warning: false,
            message: AlertMessages.SuccessUserUpdate,
            title: 'Success',
          })
        );
        dispatch(new SetUser(result.user));
      }),
      catchError(() => {
        dispatch(
          new SetNotification({
            error: true,
            warning: false,
            message: AlertMessages.ErrorUserUpdate,
            title: 'Error',
          })
        );
        return of(undefined);
      })
    );
  }

  @Action(Login)
  login({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: Login) {
    const loginData = action.loginData;
    if (loginData && loginData.email) {
      const { homeAccountId, updateUserData, ...newLoginData } = loginData;
      this.loginService.login(newLoginData).subscribe(result => {
        const newUser: User = {
          _id: '',
          id: result.id,
          email: result.email,
          firstName: result.firstName,
          lastName: result.lastName,
          displayName: result.displayName,
          country: result.country,
          isAuthenticated: true,
          userToken: result.userToken,
          teamGroupDetails: result.teamGroupDetails,
        };

        patchState({
          user: { ...getState().user, ...newUser },
        });

        // Persist user to UserState (localStorage via storage plugin)
        dispatch(new SetUserFromUserState(newUser));

        // Update before login state updated.
        // Store as plain number (seconds since epoch) — NOT JSON.stringify'd to avoid type coercion bugs.
        const tokenExpiry = Math.floor(Date.now() / 1000) + 25 * 60;

        if (newUser.userToken) {
          this.userContextService.updateContext(ContextUpdateType.TOKEN, newUser.userToken, BrowserStorageType.LOCAL);
        }

        this.userContextService.updateContext(
          ContextUpdateType.TOKEN_EXPIRY,
          tokenExpiry as any,
          BrowserStorageType.LOCAL
        );

        const userData = {
          _id: 'aVal',
          email: '',
          userToken: result.userToken,
        };

        if (action.loginData.updateUserData && action.loginData.updateUserData.displayName) {
          dispatch(new UpdateUser(action.loginData.updateUserData));
        }
        dispatch(new GetCachedUser(userData));
        patchState({ selectedRoute: 'console' });
        dispatch(new GetExternalApiData());
      });
    } else {
      patchState({ selectedRoute: 'home' });
    }
  }

  @Action(InitSso)
  initSso({ patchState, dispatch }: StateContext<ApiEcosystemModel>, action: InitSso) {
    return this.ssoService.initSSO()?.subscribe((data: any) => {
      data.Expires = data.Date;
      this.logService.log(`SSO Initilialized and authorization data returned.`, 'success', 'SSO VALID 🍺 ');
      dispatch(new SetAuthData(data));
    });
  }

  @Action(SetSelectedApi)
  setSelectedApi({ getState, patchState }: StateContext<ApiEcosystemModel>, action: SetSelectedApi) {
    patchState({
      selectedApiDetail: { ...getState().selectedApiDetail, ...action.selectedApiDetail },
    });
  }

  @Action(GetApiDetail)
  getApiDetail({ getState, setState }: StateContext<ApiEcosystemModel>, action: GetApiDetail) {
    if (action.id) {
      return this.service.getApiDetail(action.id).pipe(
        tap((result: ExternalApiDetails) => {
          if (result) {
            setState(
              patch({
                selectedApiDetail: result,
              })
            );
          }
        })
      );
    } else {
      getState();
    }
  }

  @Action(SubscribeToOneApiForManyApplications)
  subscribeToOneApiForManyApplications(
    { patchState, dispatch }: StateContext<ApiEcosystemModel>,
    action: SubscribeToOneApiForManyApplications
  ) {
    return this.service.subscribeToOneApiForManyApplications(action.apiId, action.applicationIds).pipe(
      tap(result => {
        if (result && result.httpStatus !== 500) {
          dispatch(new GetExternalApplicationsData());
          patchState({ selectedRoute: 'console' });
          dispatch(
            new SetNotification({
              error: false,
              warning: false,
              message: AlertMessages.SuccessApiAdded,
              title: 'Success',
              duration: 5000,
            })
          );
        }
        if (result && result.httpStatus === 500) {
          this.progressDialogService.hideSpinner();
          dispatch(
            new SetNotification({
              message: result.message,
              error: true,
              warning: false,
              title: 'Error',
              duration: 5000,
            })
          );
        }
      })
    );
  }

  @Action(SubscribeToManyApisForOneApplication)
  subscribeToManyApisForOneApplication(
    { dispatch }: StateContext<ApiEcosystemModel>,
    action: SubscribeToManyApisForOneApplication
  ) {
    return this.service.subscribeToManyApisForOneApplication(action.applicationId, action.apis).pipe(
      switchMap(result => {
        if (result && result.httpStatus !== 500) {
          return dispatch(new GetApplicationByID(action.applicationId)).pipe(
            tap(() =>
              dispatch(
                new SetNotification({
                  error: false,
                  warning: false,
                  message: AlertMessages.SuccessApisAdded,
                  title: 'Success',
                  duration: 5000,
                })
              )
            )
          );
        }
        if (result && result.httpStatus === 500) {
          this.progressDialogService.hideSpinner();
          dispatch(
            new SetNotification({
              message: result.message,
              error: true,
              warning: false,
              title: 'Error',
              duration: 5000,
            })
          );
        }
        return of(result);
      })
    );
  }

  @Action(CreateApplication)
  createApplication({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: CreateApplication) {
    if (action.createApplicationData) {
      return this.service.createApplication(action.createApplicationData).pipe(
        tap(result => {
          if (result) {
            dispatch(new GetExternalApplicationsData());
            patchState({ ...getState() });
          }
        })
      );
    } else {
      getState();
    }
  }

  @Action(UpdateAppWebsite)
  updateAppWebsite({ getState, dispatch }: StateContext<ApiEcosystemModel>, action: UpdateAppWebsite) {
    if (action.updateAppWebsite) {
      return this.service.updateApplicationWebsite(action.updateAppWebsite).pipe(
        tap(result => {
          if (result) {
            dispatch(new GetExternalApplicationsData());
            dispatch(new SetSelectedApplication(result));
          }
        })
      );
    }
  }

  @Action(UpdateAppOwner)
  updateAppOwner({ getState, dispatch }: StateContext<ApiEcosystemModel>, action: UpdateAppOwner) {
    if (action.updateAppOwner) {
      return this.service.updateApplicationOwner(action.updateAppOwner).pipe(
        tap(result => {
          if (result) {
            dispatch(new GetExternalApplicationsData());
            dispatch(new SetSelectedApplication(result));
            dispatch(
              new SetNotification({
                error: false,
                warning: false,
                message: AlertMessages.OwnerUpdate,
                title: 'Success',
              })
            );
            setTimeout(() => {
              this.router.navigate(['/console']);
            }, 2000);
          }
        })
      );
    }
  }

  @Action(UpdateDelegateIds)
  updateDelegateIds({ getState, dispatch }: StateContext<ApiEcosystemModel>, action: UpdateDelegateIds) {
    if (action.updateDelegateIds) {
      return this.service.updateApplicationDelegateIds(action.updateDelegateIds).pipe(
        tap(result => {
          if (result) {
            dispatch(new GetExternalApplicationsData());
            dispatch(new SetSelectedApplication(result));
          }
        })
      );
    }
  }

  @Action(AddUri)
  addUri({ getState, dispatch }: StateContext<ApiEcosystemModel>, action: AddUri) {
    if (action.addUriData) {
      this.progressDialogService.showSpinner();
      const isPayerOrProvider = action.addUriData.persona === 'payer' || action.addUriData.persona === 'provider';
      const hasProdPublicJwks =
        action.addUriData.prodPublicJwks !== undefined &&
        action.addUriData.prodPublicJwks !== null &&
        action.addUriData.prodPublicJwks !== '';
      const hasSandboxPublicJwks =
        action.addUriData.sandboxPublicJwks !== undefined &&
        action.addUriData.sandboxPublicJwks !== null &&
        action.addUriData.sandboxPublicJwks !== '';

      if (action.addUriData.isProd) {
        const { ...addUriData } = action.addUriData;
        const shouldUseJwksEndpoint = isPayerOrProvider || hasProdPublicJwks;
        const request$ = shouldUseJwksEndpoint
          ? this.service.appProdJwksUrl(addUriData)
          : this.service.addProdUri(addUriData);

        return request$.pipe(
          tap(result => {
            if (result) {
              dispatch(new GetExternalApplicationsData());
              dispatch(new GetApplicationByID(action.addUriData.appId));
            }
          })
        );
      }

      const { ...addUriData } = action.addUriData;
      const shouldUseJwksEndpoint = isPayerOrProvider || hasSandboxPublicJwks;
      const request$ = shouldUseJwksEndpoint
        ? this.service.appSandboxJwksUrl(addUriData)
        : this.service.addSandboxUri(addUriData);

      return request$.pipe(
        tap(result => {
          if (result) {
            dispatch(new GetExternalApplicationsData());
            dispatch(new GetApplicationByID(action.addUriData.appId));
          }
        })
      );
    } else {
      getState();
    }
  }

  @Action(SendContactUsEmail)
  sendContactUsEmail({ dispatch }: StateContext<ApiEcosystemModel>, action: SendContactUsEmail) {
    return this.service.sendContactUsEmail(action.contactUsData).pipe(
      map(result => {
        dispatch(
          new SetNotification({
            error: false,
            warning: false,
            message: AlertMessages.SuccessEmail,
            title: 'Success',
          })
        );
        this.location.back();
        return result;
      }),
      catchError(() => {
        dispatch(
          new SetNotification({
            error: true,
            warning: false,
            message: AlertMessages.ErrorResetPassword,
            title: 'Error',
          })
        );
        return of(undefined);
      })
    );
  }

  @Action(DownloadDocument)
  downloadDocument({ patchState }: StateContext<ApiEcosystemModel>, action: DownloadDocument) {
    return this.service.getDownloadDocumentById(action.serviceID, action.documentID).pipe(
      tap((result: string) => {
        patchState({
          downloadDocumentURI: result,
          downloadDocumentAttachmentId: action.documentID,
        });
      })
    );
  }

  @Action(UnSubscribeAPI)
  unSubscribeAPI({ patchState, dispatch }: StateContext<ApiEcosystemModel>, action: UnSubscribeAPI) {
    this.progressDialogService.showSpinner();
    return this.service.unsubscribeAPI(action.appId, action.serviceId).pipe(
      tap((result: CombineApplicationsView) => {
        if (result) {
          const findApplicationById = (appId: string, apps?: Application[]): Application | undefined =>
            apps?.find(app => app.id === appId);
          const selectedApp =
            findApplicationById(action.appId, result.personalApplications) ||
            findApplicationById(action.appId, result.groupApplications);

          if (selectedApp) {
            dispatch(new SetSelectedApplication({ ...selectedApp }));
          }

          patchState({ applications: result });
        }
      }),
      catchError(error => {
        console.error('Unable to unsubscribe from the API this', error);
        return of(null);
      }),
      finalize(() => this.progressDialogService.hideSpinner()) // Ensure spinner is hidden in all cases
    );
  }

  @Action(GetApplicationByID)
  getApplicationByID({ patchState }: StateContext<ApiEcosystemModel>, action: GetApplicationByID) {
    const inFlightRequest = this.applicationByIdRequests.get(action.appId);
    if (inFlightRequest) {
      return inFlightRequest;
    }

    const request$ = this.service.getExternalApplicationByID(action.appId).pipe(
      tap(result => {
        if (result) {
          this.progressDialogService.hideSpinner();
          patchState({
            selectedApplicationDetail: result,
          });
        }
      }),
      finalize(() => {
        this.applicationByIdRequests.delete(action.appId);
      }),
      shareReplay(1)
    );

    this.applicationByIdRequests.set(action.appId, request$);
    return request$;
  }

  @Action(DeleteApplication)
  deleteApplication({ patchState }: StateContext<ApiEcosystemModel>, action: DeleteApplication) {
    return this.service.deleteApplication(action.appId).pipe(
      tap((result: CombineApplicationsView) => {
        if (result) {
          patchState({
            applications: result,
          });
        }
      })
    );
  }

  @Action(GetContactUsCategories)
  getContactUsCategories({ patchState }: StateContext<ApiEcosystemModel>, action: GetContactUsCategories) {
    return this.service.getContactUsCategories(action.supportRequestApi).pipe(
      tap(result => {
        if (result) {
          patchState({
            contactUsCategories: result,
          });
        }
      })
    );
  }

  @Action(SetTeamGroup)
  setTeamGroup({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: SetTeamGroup) {
    return this.service.setTeamGroup(action.setTeamGroup).pipe(
      tap((updatedUser: User) => {
        const state = getState();
        const token = state.user.userToken;
        if (updatedUser) {
          patchState({
            user: {
              ...updatedUser,
              userToken: token,
              isAuthenticated: true,
            },
          });
          dispatch(
            new SetNotification({
              error: false,
              warning: false,
              message: AlertMessages.GroupCreated,
              title: 'Success',
            })
          );
        }
      })
    );
  }

  @Action(AddUserToTeamGroup)
  addUserToTeamGroup({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: AddUserToTeamGroup) {
    return this.service.addUserToTeamGroup(action.addUserToTeamGroup).pipe(
      tap((updatedUser: User) => {
        if (updatedUser) {
          const emailsNotInUsers = updatedUser.teamGroupDetails?.emailsNotInUsers || [];
          const message = emailsNotInUsers.length
            ? `${AlertMessages.MemberAddedToTheGroup}. It appears ${emailsNotInUsers} belong to a different team and were not added.`
            : AlertMessages.MemberAddedToTheGroup;
          patchState({
            user: { ...getState().user, ...updatedUser },
          });

          dispatch(
            new SetNotification({
              error: false,
              warning: false,
              message,
              title: 'Success',
              duration: 10000,
            })
          );
        }
      }),
      catchError(() => {
        dispatch(
          new SetNotification({
            error: true,
            warning: false,
            message: AlertMessages.ErrorAddingMemberToTheGroup,
            title: 'Error',
          })
        );
        return of(undefined);
      })
    );
  }

  @Action(RemoveUserFromTeamGroup)
  removeUserFromTeamGroup(
    { getState, patchState, dispatch }: StateContext<ApiEcosystemModel>,
    action: RemoveUserFromTeamGroup
  ) {
    return this.service.removeUserFromTeamGroup(action.removeUserFromTeamGroup).pipe(
      tap((updatedUser: User) => {
        if (updatedUser) {
          const currentStateUser: User = getState().user;
          if (!updatedUser.teamGroupDetails) {
            const { teamGroupDetails, ...restCurrentStateUser } = currentStateUser;
            patchState({
              user: { ...restCurrentStateUser, ...updatedUser },
            });
          } else {
            // Patch the state with updatedUser directly
            patchState({
              user: { ...getState().user, ...updatedUser },
            });
          }

          dispatch(
            new SetNotification({
              error: false,
              warning: false,
              message: AlertMessages.MemberRemovedFromTheGroup,
              title: 'Success',
            })
          );
        }
      }),
      catchError(error => {
        dispatch(
          new SetNotification({
            error: true,
            warning: false,
            message: AlertMessages.ErrorRemovingMemberFromTheGroup,
            title: 'Error',
          })
        );
        return of(undefined);
      })
    );
  }

  @Action(PromoteUserToOwner)
  promoteUserToOwner({ getState, patchState, dispatch }: StateContext<ApiEcosystemModel>, action: PromoteUserToOwner) {
    return this.service.promoteUserToOwner(action.promoteRemoveUserInTeamGroup).pipe(
      tap((updatedUser: User) => {
        if (updatedUser) {
          patchState({
            user: { ...getState().user, ...updatedUser },
          });
          dispatch(
            new SetNotification({
              error: false,
              warning: false,
              message: AlertMessages.MemberPromotedToOwner,
              title: 'Success',
            })
          );
        }
      }),
      catchError(() => {
        dispatch(
          new SetNotification({
            error: true,
            warning: false,
            message: AlertMessages.ErrorMemberPromotedToOwner,
            title: 'Error',
          })
        );
        return of(undefined);
      })
    );
  }

  @Action(GetBannerData)
  getBannerData({ patchState, dispatch }: StateContext<ApiEcosystemModel>) {
    if (this.bannerDataRequest$) {
      return this.bannerDataRequest$;
    }

    this.bannerDataRequest$ = this.bannerService.getBannerData().pipe(
      tap((bannerData: any) => {
        if (bannerData) {
          patchState({ bannerData });
        }
      }),
      catchError(error => {
        // Log error for debugging but continue
        console.warn('Failed to fetch banner data:', error);
        return of(undefined);
      }),
      finalize(() => {
        this.bannerDataRequest$ = undefined;
      }),
      shareReplay(1)
    );

    return this.bannerDataRequest$;
  }

  @Action(SetBannerData)
  setBannerData({ patchState }: StateContext<ApiEcosystemModel>, action: SetBannerData) {
    patchState({ bannerData: action.bannerData });
  }
}
